import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { uploadFile as uploadFileService } from '@/services/uploadService';
import { UploadCloud, File, Loader2 } from 'lucide-react';

interface ImageUploaderProps {
  onUploadSuccess: (
    fileId: string,
    fileName: string,
    originalWidthPx?: number,
    originalHeightPx?: number
  ) => void;
  onUploadError: (errorMessage: string) => void;
}

export const ImageUploader: React.FC<ImageUploaderProps> = ({ onUploadSuccess, onUploadError }) => {
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      setFile(acceptedFiles[0]);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': ['.jpeg', '.png', '.jpg', '.gif'] },
    multiple: false,
  });

    const handleUpload = async () => {
    if (!file) return;

    setIsLoading(true);
    try {
      const response = await uploadFileService(file);
      onUploadSuccess(
        response.file.id,
        file.name,
        response.file.originalWidthPx,
        response.file.originalHeightPx
      );
      setFile(null); // Clear the file input on success
    } catch (error: any) {
      const message = error.response?.data?.message || 'An unexpected error occurred.';
      onUploadError(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="p-6 rounded-lg shadow-lg w-full max-w-md mx-auto border">
      <h3 className="text-xl font-semibold mb-4">{t('uploader.title', 'Upload Your Image')}</h3>
      <div
        {...getRootProps()}
        className={`p-8 border-2 border-dashed rounded-lg text-center cursor-pointer transition-colors
          ${isDragActive ? 'border-primary bg-primary/10' : 'border-muted-foreground/50 hover:border-primary'}`}
      >
        <input {...getInputProps()} />
        <UploadCloud className="mx-auto h-12 w-12 text-muted-foreground" />
        <p className="mt-2 text-sm text-muted-foreground">
          {isDragActive
            ? t('uploader.drop_active', 'Drop the file here ...')
            : t('uploader.drop_inactive', 'Drag & drop an image here, or click to select')}
        </p>
      </div>
      {file && (
        <div className="mt-4 p-2 border rounded-md flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <File className="h-5 w-5 text-muted-foreground" />
            <span className="text-sm">{file.name}</span>
          </div>
          <Button onClick={() => setFile(null)} variant="ghost" size="sm">X</Button>
        </div>
      )}
      <Button onClick={handleUpload} disabled={!file || isLoading} className="w-full mt-4">
        {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        {t('uploader.upload_button', 'Upload')}
      </Button>
    </div>
  );
};
